import { useEffect, useMemo, useRef, useState } from 'react';
import type { FC, ReactNode } from 'react';
import { Select, Input } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import type { ColumnType, ColumnsType } from 'antd/es/table';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { IpcButton, IpcTable } from './ui';
import { IPC_FILTER_MULTI_SELECT_PROPS } from './ui/ipcFilterSelect';
import './TableSelect.css';

export interface TableSelectColumn<T extends object> {
  key: string;
  title: string;
  width?: number;
  render: (row: T) => ReactNode;
  searchText?: (row: T) => string;
}

export interface TableSelectProps<T extends object> {
  value: string[];
  onChange: (value: string[]) => void;
  options: T[];
  rowKey: (row: T) => string;
  columns: TableSelectColumn<T>[];
  placeholder?: string;
  disabled?: boolean;
  getTagLabel: (row: T) => string;
}

export function TableSelect<T extends object>({
  value,
  onChange,
  options,
  rowKey,
  columns,
  placeholder,
  disabled,
  getTagLabel,
}: TableSelectProps<T>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState<string[]>([]);
  const [dropdownWidth, setDropdownWidth] = useState<number>();

  const optionMap = useMemo(() => {
    const map = new Map<string, T>();
    options.forEach(row => map.set(rowKey(row), row));
    return map;
  }, [options, rowKey]);

  useEffect(() => {
    if (open) {
      setPending([...value]);
      setSearch('');
      if (rootRef.current) {
        setDropdownWidth(rootRef.current.getBoundingClientRect().width);
      }
    }
  }, [open, value]);

  const filteredOptions = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return options;
    return options.filter(row => columns.some(col => {
      const text = col.searchText ? col.searchText(row) : String(col.render(row) ?? '');
      return text.toLowerCase().includes(keyword);
    }));
  }, [columns, options, search]);

  const dataBaseColumns = useMemo<ColumnsType<T>>(
    () => columns.map(col => ({
      key: col.key,
      title: col.title,
      width: col.width,
      ellipsis: true,
      render: (_: unknown, row: T) => col.render(row),
    })),
    [columns],
  );

  const resizableTable = useResizableColumns(dataBaseColumns);

  const checkboxColumn: ColumnType<T> = useMemo(() => ({
    key: '__selected',
    width: 40,
    render: (_: unknown, row) => {
      const key = rowKey(row);
      const selected = pending.includes(key);
      return (
        <span className={`table-select-check ${selected ? 'table-select-check--active' : ''}`}>
          {selected ? <CheckOutlined /> : null}
        </span>
      );
    },
  }), [pending, rowKey]);

  const tableColumns: ColumnsType<T> = useMemo(
    () => [checkboxColumn, ...resizableTable.columns],
    [checkboxColumn, resizableTable.columns],
  );

  const tableScroll = useMemo(
    () => resizableTable.getScroll({ y: 240 }),
    [resizableTable],
  );

  const handleConfirm = () => {
    onChange([...pending]);
    setOpen(false);
  };

  const handleClear = () => {
    setPending([]);
  };

  const toggleRow = (key: string) => {
    setPending(prev => (prev.includes(key) ? prev.filter(item => item !== key) : [...prev, key]));
  };

  const selectOptions = value
    .map(key => optionMap.get(key))
    .filter((row): row is T => !!row)
    .map(row => ({
      label: getTagLabel(row),
      value: rowKey(row),
    }));

  return (
    <div ref={rootRef} className="table-select-wrap">
      <Select
        mode="multiple"
        className="table-select"
        placeholder={placeholder}
        disabled={disabled}
        open={open}
        onOpenChange={setOpen}
        value={value}
        onChange={vals => onChange((vals as string[]) ?? [])}
        options={selectOptions}
        {...IPC_FILTER_MULTI_SELECT_PROPS}
        popupMatchSelectWidth
        getPopupContainer={() => rootRef.current ?? document.body}
        dropdownAlign={{ points: ['tl', 'bl'], offset: [0, 4] }}
        dropdownStyle={{ padding: 0, width: dropdownWidth }}
        dropdownRender={() => (
          <div
            className="table-select-dropdown"
            style={dropdownWidth ? { width: dropdownWidth } : undefined}
            onMouseDown={e => e.preventDefault()}
          >
            <div className="table-select-search">
              <Input
                className="ipc-search-input"
                placeholder="请输入"
                value={search}
                onChange={e => setSearch(e.target.value)}
                allowClear
              />
            </div>
            <IpcTable<T>
              panelClassName="table-select-table-panel"
              scrollClassName="table-select-table-scroll"
              className={`table-select-table ${resizableTable.tableClassName}`}
              size="small"
              rowKey={row => rowKey(row)}
              columns={tableColumns}
              components={resizableTable.components}
              tableLayout={resizableTable.tableLayout}
              dataSource={filteredOptions}
              pagination={false}
              scroll={tableScroll}
              onRow={row => ({
                onClick: () => toggleRow(rowKey(row)),
              })}
              rowClassName={row => (pending.includes(rowKey(row)) ? 'table-select-row--active' : '')}
              compact
              frameless
            />
            <div className="table-select-footer">
              <IpcButton size="small" variant="secondary" onClick={handleClear}>清空</IpcButton>
              <IpcButton size="small" variant="primary" onClick={handleConfirm}>确定</IpcButton>
            </div>
          </div>
        )}
      />
    </div>
  );
}

export default TableSelect as FC<TableSelectProps<Record<string, unknown>>>;
